

import { useEffect, useState } from "react";
import { IconButton, CircularProgress, Switch } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import Select from "react-select";
import { Paperclip, Eye, EyeOff } from "lucide-react";
import { axiosInstance } from "../../../utility/apiInstance";
import { FlagsPropsType, Message, Option } from "./types/ActionTypes";
import toast from "react-hot-toast";

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "38px",
    borderRadius: "8px",
    borderColor: state.isFocused ? "#F8B71F" : "#e2e2e2",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(248,183,31,0.18)" : "none",
    "&:hover": { borderColor: "#F8B71F" },
    fontSize: "12px",
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: "12px",
    background: state.isSelected ? "#F8B71F" : state.isFocused ? "#fef9c3" : "#fff",
    color: state.isSelected ? "#1a1a1a" : "#374151",
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

function TabActionsTaken({ trackingNumber, uniqueValue }: FlagsPropsType) {
  const [flagsList, setFlagsList] = useState<Option[]>([]);
  const [selectedFlag, setSelectedFlag] = useState<Option | null>(null);
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string>("");
  const [fileKey, setFileKey] = useState<string>("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isViewable, setIsViewable] = useState<boolean>(false);

  const fetchFlags = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`/api/v1/shipment-flag?awbNumber=${trackingNumber}&uniqueValue=${uniqueValue}`);
      const options = (data.data || []).map((item: any) => ({
        label: `${item.flagName} - ${item.leg || "N/A"}`,
        value: item,
      }));
      setFlagsList(options);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!trackingNumber) return;
    fetchFlags();
  }, [trackingNumber]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`/api/v1/shipment-flag/action-taken?trackingNumber=${trackingNumber}&uniqueValue=${uniqueValue}`);
      setMessages(data.data || []);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!trackingNumber) return;
    fetchMessages();
  }, [trackingNumber, refreshTrigger]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    if (!selectedFile) return;

    setFile(selectedFile);
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const { data } = await axiosInstance.post("/api/v1/s3/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          const percent = Math.round((event.loaded * 100) / (event.total || 1));
          setUploadProgress(percent);
        },
      });

      const uploadedUrl = data.data.url || "";
      const uploadedFileKey = data.data.key || "";
      setFileUrl(uploadedUrl);
      setFileKey(uploadedFileKey);
      toast.success("File uploaded successfully");
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.error || err?.response?.data?.message || "failed to upload file";
      toast.error(errorMessage);
      setFile(null);
      setFileUrl("");
      setFileKey("");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      e.target.value = "";
    }
  };

  const handleDeleteFile = async () => {
    if (!fileKey) return;
    try {
      await axiosInstance.post(`/api/v1/s3/delete`, { fileKey });
      setFile(null);
      setFileUrl("");
      setFileKey("");
      toast.success("File deleted successfully");
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.error || err?.response?.data?.message || "Failed to delete file";
      toast.error(errorMessage);
    }
  };

  const handleSend = async () => {
    if (!text.trim()) {
      toast.error("Please enter action details");
      return;
    }
    if (!selectedFlag) {
      toast.error("Please select a flag first");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        flagShipmentId: selectedFlag.value._id,
        textMessage: text.trim(),
        trackingNumber,
        attachment: fileUrl || "",
        isViewable: isViewable,
        uniqueValue
      };

      await axiosInstance.post(`/api/v1/shipment-flag/action`, payload);
      toast.success("Action saved successfully");

      setText("");
      setFile(null);
      setFileUrl("");
      setFileKey("");
      setUploadProgress(0);
      setSelectedFlag(null);
      setIsViewable(false);
      setRefreshTrigger((prev) => prev + 1);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <div className="w-[3px] h-4 bg-[#F8B71F] rounded-full" />
          <span className="text-[12px] font-semibold text-gray-700 uppercase tracking-[0.06em]">
            Actions Taken
          </span>
        </div>
        <span className="text-[11px] text-gray-400 font-medium">
          {messages.length} message{messages.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="h-[260px] overflow-y-auto p-2 bg-gray-50">
        {loading && (
          <div className="flex justify-center my-2">
            <CircularProgress size={22} />
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="h-full flex items-center justify-center text-xs text-gray-400">
            No actions found.
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg._id} className="flex justify-end mb-2.5">
            <div className="w-[250px] rounded-lg overflow-hidden shadow-sm border border-green-200 bg-green-50">
              <div className="bg-green-500 text-white text-[11px] font-semibold px-2 py-1 flex justify-between">
                <span>{msg.flagName}</span>
                {msg.reportedDate && (
                  <span className="text-[10px] opacity-90">
                    {new Date(msg.reportedDate).toLocaleDateString()}
                  </span>
                )}
              </div>
              <div className="px-2.5 py-2 text-xs text-gray-800">
                <p>{msg.textMessage}</p>
                {msg.attachment && (
                  <a
                    href={msg.attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline text-[11px] mt-1 inline-block"
                  >
                    View Attachment
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {uploading && (
        <p className="text-blue-600 text-xs px-3 pt-1">Uploading: {uploadProgress}%</p>
      )}

      {fileUrl && (
        <div className="flex items-center gap-3 px-3 py-2 border-t border-gray-100 bg-gray-50">
          {file?.type.startsWith("image/") ? (
            <img src={fileUrl} alt="Preview" className="w-16 h-16 object-cover rounded-md border border-gray-200" />
          ) : (
            <div className="flex items-center gap-1.5 text-xs text-gray-600 max-w-[260px] truncate">
              <Paperclip size={14} />
              <span className="truncate">{file?.name}</span>
            </div>
          )}
          <IconButton size="small" color="error" onClick={handleDeleteFile}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      )}

      <div className="border-t border-gray-100 bg-white p-3 grid gap-2.5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-[290px]">
            <Select
              name="flag"
              placeholder="Select flag"
              options={flagsList}
              value={selectedFlag}
              onChange={(selected) => setSelectedFlag(selected)}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              menuPlacement="auto"
            />
          </div>

          <div className="inline-flex items-center justify-between min-w-[180px] border border-gray-200 rounded-lg px-2 py-1">
            <span className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
              {isViewable ? <Eye size={14} /> : <EyeOff size={14} />}
              {isViewable ? "Viewable" : "Non-viewable"}
            </span>
            <Switch
              checked={isViewable}
              onChange={(e) => setIsViewable(e.target.checked)}
              color="primary"
            />
          </div>
        </div>

        {/* replaced TextField multiline to avoid ResizeObserver loop */}
        <div className="relative">
          <textarea
            className="w-full min-h-[84px] max-h-[120px] resize-y rounded-lg border border-gray-300 p-3 pr-24 text-sm outline-none focus:border-[#F8B71F] focus:ring-2 focus:ring-[#F8B71F]/20"
            placeholder="Enter Action Details"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="absolute right-2 bottom-2 flex items-center gap-1">
            <IconButton component="label" size="small" disabled={uploading}>
              <AttachFileIcon fontSize="small" />
              <input type="file" hidden onChange={handleFileChange} />
            </IconButton>
            <IconButton onClick={handleSend} color="primary" disabled={uploading || loading} size="small">
              <SendIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TabActionsTaken;